<template>
  <SoDialog
    :model-value="modelValue"
    :loading="accountPrimaryContact?.isFetching"
    card-title="Create a new user"
    data-testid="create-user-modal"
    max-width="470"
    @update:model-value="$emit('update:modelValue', $event)"
    @on-close="$emit('close')"
  >
    <SoErrorBoundary>
      <SoForm data-testid="create-user-form" @submit="handleSubmit()">
        <SoTextField
          :model-value="accountNumber"
          :rules="[rules.required]"
          data-testid="create-user-account-input"
          label="Account Number"
          name="accountNumber"
          class="mb-5"
          disabled
        />

        <SoTextField
          v-model="form.email"
          :rules="[rules.required, rules.email]"
          data-testid="create-user-email-input"
          label="User email address"
          placeholder="john.doe@example.com"
          type="text"
          name="email"
          class="mb-5"
          autofocus
        />

        <SoCheckbox
          v-model="form.skipEmail"
          data-testid="create-user-skip-email-input"
          label="Don't send Welcome Pack email"
          class="mt-2"
        />

        <SoCheckbox
          v-model="form.shouldCreateAccountContact"
          data-testid="create-user-skip-email-input"
          label="Create dummy Junifer contact matching user email"
        />

        <SoAlert
          v-if="error"
          title="Error"
          :text="error"
          type="error"
          class="mt-4"
          data-testid="create-user-error-alert"
        />
        <div class="d-flex justify-space-between mt-8">
          <SoButton type="secondary" title="Cancel" large @click="close()" />

          <SoButton
            :loading="isUpdating"
            type="success"
            title="Create user"
            large
            submit
            data-testid="create-user-submit-btn"
          />
        </div>
      </SoForm>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"

  import { useSnackbarStore } from "@/stores/snackbar"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import rules from "@/validators/rules"
  import SoAlert from "@/components/SoAlert.vue"
  import { UserService } from "@/services/admin/UserService"
  import { getPrimaryContact } from "@/services-handlers/getAccountContacts"
  import { ContactMutator } from "@/mutators/contact/ContactMutator"
  import { capitalize } from "@/helpers/stringHelpers"
  import { UserMutator } from "@/mutators/user/UserMutator"
  import { CreateUserFromJuniferResponse } from "@/api/backofficeRest/generated/model"
  import { errorHandler, errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoForm,
      SoButton,
      SoTextField,
      SoCheckbox,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
      accountNumber: {
        type: String as PropType<string>,
        default: "",
      },
    },
    emits: ["update:modelValue", "close", "success"],
    data() {
      return {
        isUpdating: false,
        snackbar: useSnackbarStore(),
        form: {
          skipEmail: true,
          email: "",
          shouldCreateAccountContact: false,
        },
        error: "",
        rules,
      }
    },

    computed: {
      accountPrimaryContact() {
        if (!this.accountNumber) return null
        return getPrimaryContact(this.accountNumber)
      },
    },

    watch: {
      accountPrimaryContact: {
        handler(contact: typeof this.accountPrimaryContact) {
          let email = contact?.data?.email

          if (email === "redacted@redacted.null") {
            email = `${this.accountNumber}@example.com`
          }

          this.form.email = email ?? ""
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      async createAccountContact(email: string) {
        const { mutateAsync } = ContactMutator.addForAccount()

        // Dummy data for creating a new contact. Used only for testing purposes.
        return mutateAsync({
          email: email,
          accountNumber: this.accountNumber,
          title: "Dr",
          forename: capitalize(email.split("@")[0]),
          surname: "Test",
          contactType: "Residential",
          isPrimary: false,
          address: {
            address1: "1 Test Street",
            address2: "Test Town",
            address3: "Test County",
            address4: null,
            address5: null,
            postcode: "TE5T 1NG",
          },
        })
      },
      async handleSubmit() {
        const newEmail = this.form.email.trim()
        this.isUpdating = true
        this.error = ""

        try {
          const isEmailAvailable = await this.checkEmailAvailability(newEmail)

          if (!isEmailAvailable)
            throw Error(`Email ${newEmail} is already taken`)

          if (this.form.shouldCreateAccountContact) {
            await this.createAccountContact(newEmail)
          }

          await this.createUser(
            this.accountNumber,
            this.form.skipEmail,
            newEmail,
          )
          this.snackbar.addSuccess(`Successfully created user with ${newEmail}`)
          this.close()
          this.$emit("success")
        } catch (error) {
          this.mapError(error)
          errorReporter.report(error)
          console.error(error)
        } finally {
          this.isUpdating = false
        }
      },
      mapError(error) {
        // This flow throws its own Errors for validation, so their message is
        // the better fallback than a generic one.
        this.error = errorHandler.userMessage(
          error,
          error instanceof Error ? error.message : "Something went wrong",
        )
      },
      async checkEmailAvailability(email: string) {
        const query = await UserService.isEmailAvailable(email).suspense?.()
        return !!query?.data
      },

      async createUser(
        accountNumber: string,
        skipEmail: boolean,
        email: string,
      ): Promise<string> {
        const { mutateAsync } = UserMutator.createFromJunifer()

        const response = (await mutateAsync({
          skipEmail,
          accountNumber,
          email,
        })) as CreateUserFromJuniferResponse

        if (response?.email) return response.email

        throw Error("Failed to create user account")
      },
      close() {
        this.$emit("update:modelValue", false)
      },
    },
  })
</script>
